import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Quanto uma requisição precisa demorar para o overlay aparecer. A grande
 * maioria das chamadas responde antes disso e nunca chega a piscar na tela.
 */
const SHOW_DELAY_MS = 250;

/**
 * Uma vez visível, o overlay permanece ao menos esse tempo. Evita o flash de
 * "apareceu e sumiu" quando a resposta chega logo depois de SHOW_DELAY_MS.
 */
const MIN_VISIBLE_MS = 400;

/**
 * Trava de segurança. Se alguma requisição nunca finalizar — aba congelada em
 * segundo plano pelo navegador, conexão derrubada sem erro — o overlay se
 * libera sozinho em vez de deixar a tela travada até o usuário recarregar.
 */
const WATCHDOG_MS = 60000;

/**
 * Janela, a partir da última interação do usuário, em que uma requisição ainda
 * conta como "resposta a algo que ele fez". Fora dela a chamada é tratada como
 * de segundo plano e não bloqueia a tela.
 *
 * 5s é folgado de propósito: cobre debounce de busca, blur de campo e cadeias
 * de requisições encadeadas, e mesmo assim fica bem abaixo do intervalo de
 * qualquer polling da aplicação (o mais curto é de 5s numa tela de aguardo, e
 * esse já é silenciado explicitamente).
 */
const INTERACTION_WINDOW_MS = 5000;

/** Eventos que caracterizam ação direta do usuário. */
const INTERACTION_EVENTS = [
  'pointerdown',
  'keydown',
  'wheel',
  'touchstart',
  'change',
  'focusout',
  'submit',
];

@Injectable({
  providedIn: 'root',
})
export class LoaderService {
  /** Contador de `disable()`/`enable()`: > 0 significa overlay suprimido. */
  private stop = 0;

  /** O carregamento da página conta como a interação inicial. */
  private lastInteractionAt = Date.now();

  /** Requisições em andamento que devem exibir o overlay. */
  private pending = 0;

  private shownAt = 0;

  private showTimer: any;

  private hideTimer: any;

  private watchdogTimer: any;

  private readonly visible = new BehaviorSubject<boolean>(false);

  public readonly visible$: Observable<boolean> = this.visible.asObservable();

  constructor() {
    this.watchInteractions();
  }

  /**
   * Verdadeiro quando a requisição que está começando pode ser atribuída a uma
   * ação direta do usuário — clique, tecla, troca de valor, blur, envio de
   * formulário — ou ao encadeamento imediato de uma delas.
   *
   * É o critério padrão do overlay: sem gesto recente, a chamada é de segundo
   * plano e a tela não é bloqueada. Serve de rede de segurança para pollings e
   * rotinas que apareçam no futuro sem ninguém lembrar de marcar `skipLoader()`.
   */
  public get triggeredByUser(): boolean {
    return Date.now() - this.lastInteractionAt <= INTERACTION_WINDOW_MS;
  }

  /**
   * Registra o início de uma requisição que pode exibir o overlay. Deve ser
   * sempre pareado com `pop()`.
   */
  public push(): void {
    this.pending++;
    this.restartWatchdog();

    if (this.pending === 1) {
      this.requestShow();
    }
  }

  /** Registra o fim de uma requisição iniciada com `push()`. */
  public pop(): void {
    if (this.pending === 0) {
      return;
    }

    this.pending--;

    // Uma chamada de usuário costuma disparar a próxima assim que responde
    // (carrega a lista, depois os detalhes). Renovar a janela aqui mantém o
    // overlay ao longo da cadeia inteira, sem depender de um novo gesto.
    this.lastInteractionAt = Date.now();

    if (this.pending === 0) {
      this.clearWatchdog();
      this.requestHide();
    }
  }

  /**
   * Libera o overlay imediatamente e descarta a contagem pendente. Usado pelo
   * watchdog e disponível para telas que precisem destravar a UI na marra.
   */
  public reset(): void {
    this.pending = 0;
    this.clearWatchdog();
    this.clearTimer('showTimer');
    this.clearTimer('hideTimer');
    this.visible.next(false);
  }

  public enable(): void {
    this.stop = Math.max(this.stop - 1, 0);
  }

  public disable(): void {
    this.stop++;
  }

  public get active(): boolean {
    return this.stop === 0;
  }

  private requestShow(): void {
    this.clearTimer('hideTimer');

    if (this.visible.value || this.showTimer) {
      return;
    }

    this.showTimer = setTimeout(() => {
      this.showTimer = undefined;
      this.shownAt = Date.now();
      this.visible.next(true);
    }, SHOW_DELAY_MS);
  }

  private requestHide(): void {
    this.clearTimer('showTimer');

    if (!this.visible.value) {
      return;
    }

    const remaining = Math.max(MIN_VISIBLE_MS - (Date.now() - this.shownAt), 0);

    this.clearTimer('hideTimer');
    this.hideTimer = setTimeout(() => {
      this.hideTimer = undefined;
      this.visible.next(false);
    }, remaining);
  }

  private restartWatchdog(): void {
    this.clearWatchdog();
    this.watchdogTimer = setTimeout(() => this.reset(), WATCHDOG_MS);
  }

  private clearWatchdog(): void {
    this.clearTimer('watchdogTimer');
  }

  /**
   * Escuta os gestos na fase de captura, para registrar a interação antes de
   * qualquer handler da aplicação disparar a requisição correspondente.
   */
  private watchInteractions(): void {
    if (typeof document === 'undefined') {
      return;
    }

    const mark = () => {
      this.lastInteractionAt = Date.now();
    };

    INTERACTION_EVENTS.forEach((event) =>
      document.addEventListener(event, mark, { capture: true, passive: true })
    );
  }

  private clearTimer(name: 'showTimer' | 'hideTimer' | 'watchdogTimer'): void {
    if (this[name]) {
      clearTimeout(this[name]);
      this[name] = undefined;
    }
  }
}
